import { useEffect, useState } from "react"
import { Autocomplete, Box, InputAdornment, TextField } from "@mui/material"
import { useTheme, alpha } from "@mui/material/styles"
import Iconify from "minimals-template/components/Iconify"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { FlushGraphQLCache, RefreshSchedule } from "event-definitions"
import { getAllCodesOfPractice } from "./controller/get-all-codes-of-practice.controller"
import { getCopPreviewId, setCopPreviewId, subscribeCopPreview } from "./cop-preview-state"

export function CopPreviewSelector() {
    const theme = useTheme()
    const allCops = getAllCodesOfPractice.useResults({}, withInitialValue(IS_LOADING))
    const [selected, setSelected] = useState(getCopPreviewId)

    useEffect(subscribeToExternalChanges, [])

    if (allCops === IS_LOADING) return null
    if (!allCops?.length) return null

    const options = allCops.map((c) => ({ id: c._id, label: c.name }))
    const value = options.find((o) => o.id === selected) ?? null

    return (
        <Box sx={{ minWidth: 240, maxWidth: 380 }}>
            <Autocomplete
                size="small"
                options={options}
                value={value}
                onChange={handleChange}
                renderInput={renderInput}
                isOptionEqualToValue={matchById}
                disableClearable={false}
                sx={{
                    "& .MuiOutlinedInput-root": {
                        bgcolor: selected ? alpha(theme.palette.warning.main, 0.08) : "transparent",
                        borderColor: selected ? theme.palette.warning.main : undefined,
                    },
                }}
            />
        </Box>
    )

    function renderInput(params) {
        return (
            <TextField
                {...params}
                placeholder="Code of Practice..."
                size="small"
                inputProps={{ ...params.inputProps, style: { fontSize: "0.875rem" } }}
                InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                        <InputAdornment position="start">
                            <Iconify
                                icon="mdi:book-open-variant"
                                sx={{ width: 18, height: 18, color: "text.secondary" }}
                            />
                        </InputAdornment>
                    ),
                }}
            />
        )
    }

    function matchById(option, val) {
        return option.id === val?.id
    }

    function handleChange(_, option) {
        const id = option?.id ?? null
        setCopPreviewId(id)
        setSelected(id)
        FlushGraphQLCache.raise()
        RefreshSchedule.raiseOnceDelay(300)
    }

    function subscribeToExternalChanges() {
        return subscribeCopPreview(setSelected)
    }
}
